/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/* Import components */
import StaticMermaidDiagram from '@/components/charts/StaticMermaidDiagram';

/* Import configuration */
import {
    COMPONENT_OVERVIEW_NODE_COLORS,
    type ComponentOverviewTheme,
} from '@/config/componentOverviewColors';
import {
    COMPONENT_OVERVIEW_ROUTE_OPTIONS,
    createComponentOverviewRouteDiagram,
} from '@/config/componentOverviewDiagram';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

interface ComponentOverviewTabProps {
    theme: 'light' | 'dark';
}

const componentOverviewLegendItems = [
    { label: 'Application files', prefix: 'src/', variant: 'application' },
    { label: 'Components', prefix: 'src/components/', variant: 'component' },
    { label: 'Contexts', prefix: 'src/context/', variant: 'context' },
    { label: 'External components', prefix: 'no project path', variant: 'external' },
    { label: 'Routes', prefix: 'URL path', variant: 'route' },
] as const;

function ComponentOverviewTab({ theme }: ComponentOverviewTabProps) {
    const { t } = useI18n();
    const [selectedRouteId, setSelectedRouteId] = useState(
        COMPONENT_OVERVIEW_ROUTE_OPTIONS[0].id,
    );
    const [routeFilter, setRouteFilter] = useState(COMPONENT_OVERVIEW_ROUTE_OPTIONS[0].label);
    const [isRoutePickerOpen, setIsRoutePickerOpen] = useState(false);
    const [activeRouteId, setActiveRouteId] = useState<string | null>(
        COMPONENT_OVERVIEW_ROUTE_OPTIONS[0].id,
    );
    const selectedRoute = COMPONENT_OVERVIEW_ROUTE_OPTIONS.find(({ id }) => id === selectedRouteId)
        ?? COMPONENT_OVERVIEW_ROUTE_OPTIONS[0];
    const filteredRouteOptions = COMPONENT_OVERVIEW_ROUTE_OPTIONS.filter(({ label }) =>
        label.toLocaleLowerCase().includes(routeFilter.toLocaleLowerCase()),
    );
    const diagram = createComponentOverviewRouteDiagram(selectedRouteId, theme);

    function selectRoute(id: string): void {
        const route = COMPONENT_OVERVIEW_ROUTE_OPTIONS.find((option) => option.id === id);

        if (!route) {
            return;
        }

        setSelectedRouteId(route.id);
        setRouteFilter(route.label);
        setActiveRouteId(route.id);
        setIsRoutePickerOpen(false);
    }

    return (
        <div className="flex flex-col gap-6" aria-label={t('Component Overview')}>
            <section className="flex flex-col gap-2">
                <h2>{t('Component Overview')}</h2>
                <p>{t('Explore how application routes are connected to their React components.')}</p>
            </section>
            <section className="dashboard-panel flex items-stretch justify-start !overflow-visible p-5">
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
                    <label
                        className="shrink-0 text-sm font-semibold text-base-content/80"
                        htmlFor="component-overview-route"
                    >
                        {t('Page')}
                    </label>
                    <div className="relative w-full sm:max-w-md">
                    <input
                        id="component-overview-route"
                        className="input input-bordered w-full pr-10"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-controls="component-overview-route-options"
                        aria-activedescendant={activeRouteId ? `component-overview-route-option-${activeRouteId}` : undefined}
                        aria-expanded={isRoutePickerOpen}
                        aria-haspopup="listbox"
                        autoCapitalize="none"
                        autoCorrect="off"
                        placeholder={t('Search pages')}
                        spellCheck={false}
                        value={routeFilter}
                        onBlur={() => {
                            setRouteFilter(selectedRoute.label);
                            setActiveRouteId(selectedRoute.id);
                            setIsRoutePickerOpen(false);
                        }}
                        onChange={(event) => {
                            const nextRouteFilter = event.target.value;
                            const nextActiveRoute = COMPONENT_OVERVIEW_ROUTE_OPTIONS.find(({ label }) =>
                                label.toLocaleLowerCase().includes(nextRouteFilter.toLocaleLowerCase()),
                            );

                            setRouteFilter(nextRouteFilter);
                            setActiveRouteId(nextActiveRoute?.id ?? null);
                            setIsRoutePickerOpen(true);
                        }}
                        onFocus={() => {
                            setActiveRouteId(selectedRoute.id);
                            setIsRoutePickerOpen(true);
                        }}
                        onKeyDown={(event) => {
                            const activeRouteIndex = filteredRouteOptions.findIndex(
                                ({ id }) => id === activeRouteId,
                            );

                            if (event.key === 'ArrowDown' && filteredRouteOptions.length > 0) {
                                event.preventDefault();
                                setIsRoutePickerOpen(true);
                                setActiveRouteId(
                                    filteredRouteOptions[Math.min(activeRouteIndex + 1, filteredRouteOptions.length - 1)].id,
                                );
                            }

                            if (event.key === 'ArrowUp' && filteredRouteOptions.length > 0) {
                                event.preventDefault();
                                setIsRoutePickerOpen(true);
                                setActiveRouteId(
                                    filteredRouteOptions[Math.max(activeRouteIndex - 1, 0)].id,
                                );
                            }

                            if (event.key === 'Enter' && activeRouteId) {
                                event.preventDefault();
                                selectRoute(activeRouteId);
                            }

                            if (event.key === 'Escape') {
                                setRouteFilter(selectedRoute.label);
                                setActiveRouteId(selectedRoute.id);
                                setIsRoutePickerOpen(false);
                            }
                        }}
                    />
                    <ChevronDown
                        aria-hidden="true"
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/60"
                    />
                    {isRoutePickerOpen && (
                        <div
                            id="component-overview-route-options"
                            role="listbox"
                            className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
                        >
                            {filteredRouteOptions.length > 0 ? (
                                filteredRouteOptions.map(({ id, label }) => (
                                    <button
                                        key={id}
                                        id={`component-overview-route-option-${id}`}
                                        type="button"
                                        role="option"
                                        aria-selected={id === selectedRouteId}
                                        className={`flex w-full rounded-btn px-3 py-2 text-left font-mono text-sm transition-colors ${id === activeRouteId ? 'bg-base-200 font-semibold text-base-content' : 'text-base-content/80 hover:bg-base-200'}`}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                        }}
                                        onClick={() => {
                                            selectRoute(id);
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))
                            ) : (
                                <p className="px-3 py-2 text-sm text-base-content/60">
                                    {t('No pages found.')}
                                </p>
                            )}
                        </div>
                    )}
                    </div>
                </div>
            </section>
            <section
                className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
                aria-label={t('Legend')}
            >
                {componentOverviewLegendItems.map(({ label, prefix, variant }) => {
                    const colors = COMPONENT_OVERVIEW_NODE_COLORS[theme as ComponentOverviewTheme][variant];

                    return (
                        <div
                            key={label}
                            className="rounded-box border p-3"
                            style={{
                                backgroundColor: colors.backgroundColor,
                                borderColor: colors.borderColor,
                                color: colors.textColor,
                            }}
                        >
                            <p className="font-semibold">{t(label)}</p>
                            <code className="mt-1 block w-fit rounded-sm border border-current/30 bg-black/10 px-1.5 py-0.5 font-mono text-xs">
                                {t(prefix)}
                            </code>
                        </div>
                    );
                })}
            </section>
            <section className="dashboard-panel">
                <StaticMermaidDiagram definition={diagram} theme={theme} />
            </section>
        </div>
    );
}

export default ComponentOverviewTab;
